// In src/pages/GroupForm.js

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db } from '../services/firebase';
import { doc, getDoc, setDoc, addDoc, collection, serverTimestamp, query, where, getDocs, orderBy as firestoreOrderBy } from 'firebase/firestore'; // Import orderBy

import { Container, TextField, Button, Typography, Paper, Box, FormControlLabel, Switch, FormControl, InputLabel, Select, MenuItem } from '@mui/material';

function GroupForm() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [leaderId, setLeaderId] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [churchMembers, setChurchMembers] = useState([]);
  const [loading, setLoading] = useState(false);

  const isEditing = Boolean(groupId);

  // Fetch all church members to populate the leader selection dropdown
  useEffect(() => {
    if (userProfile?.churchId) {
      const usersColRef = collection(db, 'users');
      // MODIFIED: Removed the client-side role filtering from the Firestore query
      // Fetch all active members from the current church.
      let usersQuery = query(
        usersColRef, 
        where('churchId', '==', userProfile.churchId),
        where('status', '==', 'active'), // Ensure only active members can be leaders
        firestoreOrderBy('lastName'), 
        firestoreOrderBy('firstName')
      );

      getDocs(usersQuery).then(snapshot => {
        // Now, filter out Super Administrators client-side if the current viewer is not a Super Admin.
        // The security rules allow Church Admins to read Super Admin profiles in their church,
        // so fetching them is fine; the client then filters for display.
        const isCurrentViewerSuperAdmin = userProfile?.role === 'Super Administrator';
        let fetchedMembers = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        if (!isCurrentViewerSuperAdmin) {
          fetchedMembers = fetchedMembers.filter(member => member.role !== 'Super Administrator');
        }
        setChurchMembers(fetchedMembers);
      }).catch(error => {
        console.error("Error fetching church members:", error);
        // Handle error appropriately, e.g., set an error message in state
      });
    }
  }, [userProfile]);

  useEffect(() => {
    if (isEditing && userProfile?.churchId) {
      const groupDocRef = doc(db, 'churches', userProfile.churchId, 'groups', groupId);
      getDoc(groupDocRef).then(docSnap => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          setName(data.name || '');
          setDescription(data.description || '');
          setLeaderId(data.leaderId || '');
          setIsPublic(data.isPublic ?? true);
        }
      });
    }
  }, [isEditing, groupId, userProfile]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const selectedLeader = churchMembers.find(m => m.id === leaderId);

    const groupData = {
      name,
      description,
      leaderId,
      leaderName: selectedLeader ? `${selectedLeader.firstName} ${selectedLeader.lastName}` : 'N/A',
      isPublic,
      acceptsJoinRequests: isPublic ? false : true,
    };

    if (isEditing) {
      const groupDocRef = doc(db, 'churches', userProfile.churchId, 'groups', groupId);
      await setDoc(groupDocRef, groupData, { merge: true });
      navigate(`/groups/${groupId}`);
    } else {
      const groupsColRef = collection(db, 'churches', userProfile.churchId, 'groups');
      await addDoc(groupsColRef, {
        ...groupData,
        createdAt: serverTimestamp(),
        memberUids: [leaderId],
      });
      navigate('/groups');
    }
    setLoading(false);
  };

  return (
    <Container maxWidth="sm">
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h4" gutterBottom>
          {isEditing ? 'Edit Group' : 'Create a New Group'}
        </Typography>
        <Box component="form" onSubmit={handleSubmit}>
          <TextField label="Group Name" value={name} onChange={(e) => setName(e.target.value)} fullWidth required margin="normal" />
          <TextField label="Group Description" value={description} onChange={(e) => setDescription(e.target.value)} fullWidth multiline rows={4} margin="normal" />
          <FormControl fullWidth margin="normal">
            <InputLabel id="leader-select-label">Group Leader</InputLabel>
            <Select
              labelId="leader-select-label"
              value={leaderId}
              label="Group Leader"
              onChange={(e) => setLeaderId(e.target.value)}
            >
              {churchMembers.map(member => (
                <MenuItem key={member.id} value={member.id}>
                  {member.firstName} {member.lastName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={<Switch checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} />}
            label="Publicly Joinable"
          />
          <Button type="submit" variant="contained" disabled={loading} sx={{ mt: 2 }}>
            {loading ? 'Saving...' : 'Save Group'}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
}

export default GroupForm;